import React from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import { useCanonical, MARKETING_BASE } from "@/lib/seo";
import { Lock, FileText, Info, Hand, Cookie, ShieldCheck, Flag, MailOpen, FileSignature, ChevronLeft, ChevronRight } from "lucide-react";

export const LEGAL_ITEMS = [
  { slug: "privacy", title: "Privacy Policy", sub: "How we collect, use, and protect your data", icon: Lock },
  { slug: "terms", title: "Terms & Conditions", sub: "The agreement between you and KinBridge Health", icon: FileText },
  { slug: "disclaimer", title: "Disclaimer", sub: "Scope and limits of KinBridge information", icon: Info },
  { slug: "acceptable-use", title: "Acceptable Use", sub: "What you can and can't do with the service", icon: Hand },
  { slug: "cookies", title: "Cookie Policy", sub: "How the marketing site uses cookies", icon: Cookie },
  { slug: "security", title: "HIPAA & security", sub: "Our safeguards for protected health information", icon: ShieldCheck },
  { slug: "california", title: "California Privacy Rights", sub: "Limit the use of your sensitive personal information", icon: Flag },
  { slug: "dsar", title: "Data Access Request", sub: "Access, delete, or correct your data", icon: MailOpen },
  { slug: "eula", title: "End User Licence Agreement", sub: "Terms for using the KinBridge Health app", icon: FileSignature },
];

export function LegalShell({ title, children, backTo = "/", backLabel = "Back" }) {
  return (
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-10 bg-white border-b border-line">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <Link to={backTo} className="flex items-center gap-1 text-brand hover:text-brand-dark text-sm font-semibold">
            <ChevronLeft className="w-5 h-5" /> {backLabel}
          </Link>
          <div className="flex-1 flex items-center justify-center gap-2 -ml-16">
            <LogoMark className="h-5" />
            <span className="font-display text-lg text-ink">{title}</span>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8">{children}</main>
    </div>
  );
}

export default function Legal() {
  useCanonical(`${MARKETING_BASE}/legal`, "Legal & policies · KinBridge Health");
  return (
    <LegalShell title="Legal & policies" backTo="/" backLabel="Home">
      <p className="text-sm text-subtle mb-6">All KinBridge Health policies. Managed live via Termly — always up to date.</p>
      <div className="bg-white border border-line rounded-xl overflow-hidden">
        {LEGAL_ITEMS.map((it, i) => (
          <Link key={it.slug} to={`/legal/${it.slug}`} data-testid={`legal-row-${it.slug}`}
            className={`flex items-center gap-3 p-4 hover:bg-brand-tint transition-colors ${i > 0 ? "border-t border-line" : ""}`}>
            <div className="w-9 h-9 rounded-lg bg-brand-tint flex items-center justify-center shrink-0"><it.icon className="w-[18px] h-[18px] text-brand-dark" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-ink">{it.title}</div>
              <div className="text-xs text-subtle">{it.sub}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-subtle" />
          </Link>
        ))}
      </div>
      <p className="text-xs text-subtle text-center mt-6">Questions? Email privacy@kinbridgehealth.com</p>
    </LegalShell>
  );
}
